import { Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import sortBy from 'lodash/sortBy';
import { catchError, combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, first, map, pairwise, startWith, switchMap, takeWhile } from 'rxjs/operators';

import { ChoiceDialog, DialogService } from '../../dialogs';
import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService, UserOption } from '../../projects/projects.service';
import { ProjectPickerComponent } from '../../shared/components/project-picker/project-picker.component';
import { TypeaheadComponent } from '../../shared/components/typeahead/typeahead.component';
import { TaskViewer } from '../../shared/enums/deprecated-task.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { BulkTaskInput, TaskParentsEstimateFilter } from '../../shared/models/deprecated-task.model';
import { Option } from '../../shared/models/option.model';
import { Project, ProjectWithStats } from '../../shared/models/project.model';
import { Role } from '../../shared/models/role.model';
import { User } from '../../shared/models/user.model';
import { TaskDuplicatesDialogComponent, TaskDuplicatesResult } from '../task-duplicates-dialog/task-duplicates-dialog.component';
import { TasksService } from '../tasks.service';
import { TranslationBundle, TranslationService } from '../../translation.service';

export enum DuplicateStrategy {
  CreateAll, // duplicates were detected in dry run, create ALL tasks
  CreateClean, // NO duplicates were detected in dry run, create non-duplicates
  IgnoreDuplicates, // duplicates were detected in dry run, only create non-duplicates
}

interface ProtocolOption extends Option {
  parentId: ''
}

@Component({
  selector: 'create-task-dialog',
  templateUrl: './create-task-dialog.component.html',
  styleUrls: ['./create-task-dialog.component.scss'],
})
export class CreateTaskComponent implements OnDestroy {
  allReadProtocols: ProtocolOption[] = [];
  canDryrun = false;
  containerTypes = [{ label: 'Session', value: 'session' }, { label: 'Acquisition', value: 'acquisition' }, { label: 'File', value: 'file' }];
  createMore = this.data?.parent_type?.length > 0; // default createMore on if we're restoring from a previous create
  currentUser = '';
  excludesTagsContainer: Project;
  exhaustive = false;
  formGroup: FormGroup;
  includesTagsContainer: Project;
  loadingMatchingContainers = false;
  matchingContainerCount: number;
  projects: ProjectWithStats[] = [];
  projectsMap: Record<string, ProjectWithStats> = {};
  protocolOptions: ProtocolOption[] = [];
  requiredValidator = Validators.required; // expose for template
  roles: Role[] = [];
  shouldFetchContainerCount$: Observable<boolean>;
  taskTagsContainer: Project;
  taskTypes = [{ label: 'Read', value: 'R' }];
  userOptions: UserOption[] = [];

  @ViewChild('assigneeTypeahead', { static: false }) assigneeTypeahead: TypeaheadComponent
  @ViewChild('projectPicker', { static: false }) projectPicker: ProjectPickerComponent

  private messages = TranslationBundle.EMPTY;
  private subscriptions = {
    count: Subscription.EMPTY,
    creation: Subscription.EMPTY,
    messages: Subscription.EMPTY,
    project: Subscription.EMPTY,
    projectPermissions: Subscription.EMPTY,
    protocols: Subscription.EMPTY,
    userTaskCount: Subscription.EMPTY,
  }

  get selectedContainerLabel() : string {
    const containerType = this.formGroup.controls.container.value;
    if (['session', 'acquisition', 'file'].includes(containerType)) {
      return this.messages.get(`common.labels.${containerType}${this.matchingContainerCount === 1 ? '' : 's'}`);
    }
    return '';
  }

  set selectedContainerLabel(value: string) {} // eslint-disable-line @typescript-eslint/no-empty-function

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: BulkTaskInput,
    private dialogRef: MatDialogRef<CreateTaskComponent, BulkTaskInput>,
    private dialog: DialogService,
    private coreApi: CoreApiService,
    private profileService: ProfileService,
    private projectsService: ProjectsService,
    private translate: TranslationService,
    private notifications: NotificationService,
    private logger: LoggingService,
    private tasksService: TasksService,
  ) {
    this.formGroup = new FormGroup({
      taskType: new FormControl({ value: 'R', disabled: true }, [Validators.required]),
      project: new FormControl(this.data?.project_id, [Validators.required]),
      container: new FormControl({ value: this.data?.parent_type || '', disabled: true }, [Validators.required]),
      containerIncludesTags: new FormControl({ value: this.data?.parent_tag_filter?.include || [], disabled: true }, []),
      containerExcludesTags: new FormControl({ value: this.data?.parent_tag_filter?.exclude || [], disabled: true }, []),
      assignee: new FormControl({ value: null, disabled: true }, [Validators.required]),
      dueDate: new FormControl({ value: this.data?.due_date ? new Date(this.data.due_date) : null, disabled: true }, [this.futureDueDateValidator]),
      readProtocol: new FormControl({ value: this.data?.protocol_id, disabled: true }, [Validators.required]),
      tags: new FormControl({ value: this.data?.tags || [], disabled: true }, []),
    });
    const projectControl = this.formGroup.get('project');
    projectControl.setValidators([Validators.required, this.projectHasDataValidator]);
    this.subscriptions.project = projectControl.valueChanges.pipe(
      startWith(projectControl.value),
      switchMap(project => {
        if (!project) {
          return of([]);
        }
        return this.projectsService.getProjectUsersWithPermission(project, [PermissionAction.FormResponsesManage, PermissionAction.AnnotationsManage]);
      }),
      catchError(error => {
        this.logger.error(error);
        return of([]);
      }),
      map((users: User[]) => sortBy(users.map(user => {
        return {
          ...user,
          value: user._id,
          label: `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() || user.email || user._id,
        };
      }), user => user.label.toLowerCase())),
    ).subscribe(users => {
      this.userOptions = users;
    });
    this.shouldFetchContainerCount$ = this.formGroup.valueChanges.pipe(
      pairwise(),
      filter(([previous, current]) => {
        return previous === null
          || previous.project !== current.project
          || previous.container !== current.container
          || previous.containerIncludesTags !== current.containerIncludesTags
          || previous.containerExcludesTags !== current.containerExcludesTags;
      }),
      map(() => this.formGroup.get('container').valid && projectControl.valid),
    );
    this.subscriptions.count = this.shouldFetchContainerCount$.pipe(
      filter((value) => value),
    ).subscribe(() => {
      this.loadingMatchingContainers = true;
      const filter = this.buildTaskParentsFilter();
      this.coreApi.readerTasks.batch.parents(filter).subscribe(result => {
        this.matchingContainerCount = result.matching;
        this.loadingMatchingContainers = false;
      });
    });
    this.subscriptions.messages = this.translate.getBundle().subscribe(bundle => {
      this.messages = bundle;
    });
    this.subscriptions.protocols = this.tasksService.getReadProtocols({ refresh: true }).subscribe((protocols) => {
      const protocolOptions = [];
      for (const protocol of protocols) {
        protocolOptions.push({ label: protocol.label, parentId: protocol.parent.id, value: protocol._id });
      }
      this.allReadProtocols = protocolOptions;
    });
    this.subscriptions.projectPermissions = combineLatest([
      this.projectsService.getProjects().pipe(first()),
      this.coreApi.roles.query(),
      this.profileService.getProfile().pipe(first((v) => Boolean(v))),
      this.profileService.exhaustiveMode$,
    ]).subscribe(([projects, roles, profile, exhaustive]) => {
      this.projects = projects;
      for (const project of projects) {
        this.projectsMap[project._id] = project;
      }
      this.exhaustive = exhaustive;
      if (this.data.project_id) {
        this.selectProject({ _id: this.data.project_id }, false);
      }
      this.roles = roles.results;
      this.currentUser = profile._id;
    });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  projectWasTouched(): boolean {
    return this.projectPicker?.touched || false;
  }

  getProjectLabel(projectId: string): string {
    return this.projectsMap[projectId]?.label || '';
  }

  getProjectsFilter() {
    return (value: Project): boolean => {
      if (this.exhaustive) {
        return true;
      }
      const roleIds = value.permissions.find((permission) => permission._id === this.currentUser)?.role_ids || [];
      let hasReaderTaskAdmin = false;
      for (const roleId of roleIds) {
        if (this.roles.find((role) => role._id === roleId)?.actions.includes(PermissionAction.ReaderTaskAdmin)) {
          hasReaderTaskAdmin = true;
          break;
        }
      }
      return hasReaderTaskAdmin;
    };
  }

  selectProject(project: Partial<Project>, resetRelatedFields: Boolean = true): void {
    const fullProject = this.projects.find(p => p._id === project._id);
    if (fullProject) {
      // omit the project's tags from this unrelated list
      this.taskTagsContainer = { ...fullProject, tags: [] };
      this.includesTagsContainer = { ...fullProject, tags: this.formGroup.get('containerIncludesTags').value };
      this.excludesTagsContainer = { ...fullProject, tags: this.formGroup.get('containerExcludesTags').value };
    }
    this.formGroup.get('project').setValue(project._id);
    this.formGroup.markAsDirty();
    const fieldsToUpdate = ['taskType', 'container', 'containerIncludesTags', 'containerExcludesTags', 'assignee', 'dueDate', 'readProtocol', 'tags'];
    for (const field of fieldsToUpdate) {
      this.formGroup.get(field).enable();
    }
    if (resetRelatedFields) {
      this.formGroup.get('readProtocol').reset();
    }
    this.protocolOptions = this.allReadProtocols.filter((protocol) => protocol.parentId === project._id);
  }

  buildBulkTaskInput(): BulkTaskInput {
    const formValue = this.formGroup.getRawValue(); // use rawValue to include disabled fields
    return {
      assignees: [formValue.assignee],
      parent_type: formValue.container,
      protocol_id: formValue.readProtocol.length ? formValue.readProtocol : null,
      project_id: formValue.project,
      parent_tag_filter: {
        include: formValue.containerIncludesTags,
        exclude : formValue.containerExcludesTags,
      },
      tags: formValue.tags,
      viewer: TaskViewer.OHIF,
      task_type: formValue.taskType,
      due_date: formValue.dueDate?.toISOString ? formValue.dueDate.toISOString() : null,
      allow_duplicates: false, // if we're creating duplicates they will be created individually later
    };
  }

  buildTaskParentsFilter(): TaskParentsEstimateFilter {
    const formValue = this.formGroup.getRawValue(); // use rawValue to include disabled fields
    return {
      parent_type: formValue.container,
      project_id: formValue.project,
      include_tags: formValue.containerIncludesTags,
      exclude_tags: formValue.containerExcludesTags,
    };
  }

  submit(): void {
    if (!this.formGroup.valid) {
      return;
    }
    const task = this.buildBulkTaskInput();
    this.subscriptions.creation = this.coreApi.readerTasks.batch.duplicates(task).pipe(
      switchMap((duplicateTasks) => {
        if (duplicateTasks.count > 0) {
          return this.dialog.open(TaskDuplicatesDialogComponent, {
            data: {
              dueDate: this.formGroup.get('dueDate').value,
              duplicates: duplicateTasks.results,
              tags: this.formGroup.get('tags').value,
            },
          }).afterClosed().pipe(
            map(result => result as TaskDuplicatesResult),
            takeWhile(result => Boolean(result)), // filter out Cancel button
            switchMap(duplicateResult => {
              return this.coreApi.readerTasks.batch.create(task).pipe(
                map(createResult => ({ createResult, ...duplicateResult })));
            }),
          );
        }
        return this.coreApi.readerTasks.batch.create(task).pipe(map(result => ({ createResult: result, strategy: DuplicateStrategy.CreateClean, duplicatesCreated: 0 })));
      })
    ).subscribe({
      next: ({ createResult, strategy, duplicatesCreated }) => {
        // Refresh the tasks count in the sidebar
        this.subscriptions.userTaskCount = this.profileService.getUserTaskCount({ refresh: true }).pipe(first()).subscribe();
        this.tasksService.refreshTaskList$.next();
        if (!this.createMore) {
          this.dialogRef.close();
        } else {
          this.dialogRef.close(task);
        }
        if (createResult.length + duplicatesCreated > 0) {
          if (strategy === DuplicateStrategy.CreateAll) {
            this.notifications.success('tasks.bulkCreate.createAllSuccess');
          } else if (strategy === DuplicateStrategy.IgnoreDuplicates) {
            this.notifications.success('tasks.bulkCreate.ignoreDuplicatesSuccess');
          } else if (strategy === DuplicateStrategy.CreateClean) {
            this.notifications.success('tasks.bulkCreate.createCleanSuccess');
          }
        } else {
          this.notifications.warning('tasks.bulkCreate.noneCreated');
        }
      },
      error: error => {
        this.logger.error(error);
        this.notifications.error('tasks.bulkCreate.error');
      },
    });
  }

  async cancel(): Promise<void> {
    if (this.formGroup.pristine) {
      return this.dialogRef.close();
    }
    this.dialog.open(ChoiceDialog, {
      data: {
        multi: 'allow',
        alignActions: 'end',
        title: this.messages.get('createTaskDialog.cancel.title'),
        content: this.messages.get('createTaskDialog.cancel.content'),
        choices: [
          {
            label: this.messages.get('createTaskDialog.cancel.exit'),
            value: 'cancel',
            variant: 'stroked',
          },
          {
            label: this.messages.get('createTaskDialog.cancel.return'),
            value: 'continue',
            color: 'primary',
            variant: 'flat',
          },
        ],
      },
    }).afterClosed().pipe(
      filter(result => result === 'cancel'),
    ).subscribe(() => this.dialogRef.close());
  }

  projectHasDataValidator: ValidatorFn = control => {
    const fullProject = this.projects.find(p => p._id === control.value);
    return fullProject?.session_count === 0 ? { empty: this.messages?.get('createTaskDialog.error.emptyProject') } : null;
  }

  futureDueDateValidator: ValidatorFn = control => {
    if (!control.value) {
      return null;
    }
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return control.value < now ? { dateIsPast: true } : null;
  }

  updateAssignee(assignee: string): void {
    this.formGroup.get('assignee').setValue(assignee);
  }

  requiredAssigneeValidator: ValidatorFn = control => {
    return control.touched && !control.value ? { required: this.messages?.get('createTaskDialog.error.assigneeRequired') } : null;
  }

  noAssigneesValidator: ValidatorFn = () => {
    return (this.formGroup.get('project').value && this.userOptions.length === 0) ? { empty: this.messages?.get('createTaskDialog.error.noAssignees') } : null;
  }
}
